import * as React from 'react';
import { Dialog } from 'primereact/dialog';
import { useDispatch, useSelector } from 'react-redux';

import { alertSuccess } from '../../../../store/reducers/Alert';
import { ApplicationState } from '../../../../store';
import { PDFViewer } from '../../../../components/synced/widgets/PDFViewer';
import { WordViewer } from '../../../../components/synced/widgets/PDFViewer/components/WordViewer';
import { noteToHighlights } from '../../../../components/synced/widgets/PDFViewer/helpers/noteMapping';
import type { Highlight } from '../../../../components/synced/widgets/PDFViewer/types';
import { getConstantQueryFetchPaged } from '../../../../helpers/constants';
import { getPDFNoteStatusBadge } from '../../../../helpers/mapping/PDFNoteStatusVals';
import environment from '../../../../helpers/environment';
import { services } from '../../../../services';
import { useBackgroundPromiseWrapper } from '../../../../helpers/hooks/useBackgroundPromiseWrapper';
import { useDownload } from '../../../../helpers/hooks/useDownload';
import { PDFNote } from '../../models/PDFNote';
import { PDFNoteQuery } from '../../models/PDFNoteQuery';
import { ProjectDoc } from '../../../projectDocs/models/ProjectDoc';
import { ProjectDocFolderAttachment } from '../../../projectDocs/models/ProjectDocFolderAttachment';
import { buildContractorNotePermissionsMap } from '../../helpers/contractorNotePermissions';
import { ContractorCommentsPanel } from './ContractorCommentsPanel';
import { useAttachmentFile } from '../../../../components/synced/widgets/PDFViewer/hooks/useAttachmentFile';
import { DocumentLoading } from '../../../../components/synced/widgets/PDFViewer/components/DocumentLoading';
import { NoteUpdatesController } from '../../helpers/noteUpdates';
import { ContractorViewerNoteUpdates, createContractorViewerNoteUpdates } from '../../helpers/contractorViewerNoteUpdates';

// Воркер pdf.js лежит в public/. Приложение может быть развёрнуто не в корне
// (REACT_PUBLIC_URL), поэтому абсолютный '/pdf.worker.js' там не найдётся.
const WORKER_SRC = `${environment.baseUrl.replace(/\/$/, '')}/pdf.worker.js`;

export function isPdfAttachment(a?: ProjectDocFolderAttachment): boolean {
    if (!a) return false;
    return a.Attachment?.Type === 'application/pdf'
        || (a.Attachment?.Title ?? '').toLowerCase().endsWith('.pdf');
}

export function isWordAttachment(a?: ProjectDocFolderAttachment): boolean {
    if (!a) return false;
    const title = (a.Attachment?.Title ?? '').toLowerCase();
    return a.Attachment?.Type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        || title.endsWith('.docx')
        || title.endsWith('.doc');
}

interface ContractorViewerModalProps {
    attachment: ProjectDocFolderAttachment;
    /** Папка, замечания которой показываем (NoteObjectId). */
    projectDocFolderId: number;
    projectDocFolderStatusId: number;
    projectDoc?: ProjectDoc;
    /** Прокрутить к этому замечанию при открытии. */
    initialScrollToNoteId?: number;
    onHide: () => void;
    onNotesChanged?: () => void;
    noteUpdates?: NoteUpdatesController;
}

/**
 * Просмотр документа с замечаниями для контрагента.
 *
 * Отличия от pda-front:
 *   mode="review"       — ни рисования, ни редактирования, ни удаления геометрии
 *   commentsPanel       — своя, только на чтение и с фильтром NotForContractor
 *   noteLinkHref        — null, роута /notes/:id в этом приложении нет
 *   contractorPermissions — права считаются на клиенте: контрагентский API
 *                           не отдаёт Actions[] (см. contractorNotePermissions.ts)
 */
export const ContractorViewerModal: React.FC<ContractorViewerModalProps> = ({
    attachment,
    projectDocFolderId,
    projectDocFolderStatusId,
    projectDoc,
    initialScrollToNoteId,
    onHide,
    onNotesChanged,
    noteUpdates,
}) => {
    const dispatch = useDispatch();
    const user = useSelector((s: ApplicationState) => s.context.userContext);
    const [promiseWrapper, isActionLoading] = useBackgroundPromiseWrapper();
    const { downloadFile, isDownloading } = useDownload();

    const [notes, setNotes] = React.useState<PDFNote[] | null>(null);
    const [noteUpdatesPending, setNoteUpdatesPending] = React.useState(false);
    const [noteUpdatesError, setNoteUpdatesError] = React.useState(false);
    const [loadedViewerUpdates, setLoadedViewerUpdates] = React.useState<ContractorViewerNoteUpdates>();

    const isWord = isWordAttachment(attachment);

    // --- файл ---------------------------------------------------------------
    // Кэш + прогресс загрузки — общий с pda-front (см. docs/PDFViewer.md).
    const attachmentUrl = `Attachments/GetById?id=${attachment.AttachmentId}`;
    const file = useAttachmentFile(attachment.AttachmentId, attachmentUrl, attachment.Attachment?.Type);

    // --- замечания ----------------------------------------------------------
    const notesQuery = React.useMemo<PDFNoteQuery>(() => ({
            ...getConstantQueryFetchPaged<PDFNote>(),
            NoteObjectIds: [projectDocFolderId],
            AssigneeIds: [],
            WorkflowInstanceIds: [],
            TaskIds: [],
            NotTaskIds: [],
            CodeIds: [],
            StatusIds: [],
            NotStatusIds: [],
            Take: 1000,
            Skip: 0,
            IsDeleted: false,
            IncludeDeletedWorkflowInstances: false,
        }), [projectDocFolderId]);

    const viewerUpdates = React.useMemo(() => noteUpdates ? createContractorViewerNoteUpdates({
        query: notesQuery,
        attachmentId: attachment.Id ?? 0,
        noteUpdates,
        request: query => services.pDFNoteService.fetchPaged(query).then(response => response.data),
        onNotes: setNotes,
        onPending: setNoteUpdatesPending,
        onError: () => setNoteUpdatesError(true),
    }) : undefined, [noteUpdates, notesQuery, attachment.Id, attachment.AttachmentId]);

    React.useLayoutEffect(() => {
        viewerUpdates?.activate();
        setNoteUpdatesPending(false);
        setNoteUpdatesError(false);
        return () => viewerUpdates?.deactivate();
    }, [viewerUpdates]);

    const loadNotes = React.useCallback((isCurrent?: () => boolean) => {
        return services.pDFNoteService.fetchPaged(notesQuery).then(res => {
            // Замечания папки относятся к разным файлам — оставляем только
            // те, что привязаны к открытому вложению.
            const own = (res.data.Items ?? []).filter(n =>
                (n.NoteWorkflowInstanceAttachments ?? []).some(
                    a => a.PDFWorkflowInstanceAttachment?.ProjectDocFolderAttachmentId === attachment.Id
                )
            );
            if (!viewerUpdates || isCurrent?.()) {
                setNotes(own);
                viewerUpdates?.acceptInitial(own);
                if (viewerUpdates) setLoadedViewerUpdates(viewerUpdates);
            }
            return own;
        });
    }, [notesQuery, attachment.Id, viewerUpdates]);

    React.useEffect(() => {
        let cancelled = false;
        if (viewerUpdates) setNotes(null);
        loadNotes(() => !cancelled).catch(() => {
            if (!cancelled) {
                setNotes([]);
                if (viewerUpdates) setLoadedViewerUpdates(viewerUpdates);
            }
        });
        return () => { cancelled = true; };
    }, [loadNotes, viewerUpdates]);

    const highlights: Highlight[] | undefined = React.useMemo(() => {
        if (notes === null) return undefined;
        return notes.flatMap(n => noteToHighlights(n, {
            currentUserId: user.Id ?? 0,
            statusBadge: getPDFNoteStatusBadge,
            // Word рендерится одним потоком без страниц
            forcePageNumber: isWord ? 1 : undefined,
        }));
    }, [notes, user.Id, isWord]);

    const contractorPermissions = React.useMemo(
        () => viewerUpdates && noteUpdatesPending ? {} : buildContractorNotePermissionsMap(notes ?? [], projectDocFolderStatusId, projectDoc),
        [notes, projectDocFolderStatusId, projectDoc, viewerUpdates, noteUpdatesPending]
    );

    // Родитель может рендерить этот модал внутри карточки замечания (NoteCardActions).
    // Немедленный onNotesChanged перезагрузил бы список, замечание ушло бы из таба,
    // карточка размонтировалась — и просмотрщик схлопнулся бы прямо под руками.
    // Поэтому обновление списка откладываем до закрытия; внутри модала список
    // замечаний обновляем сами, а канал сообщает родителю изменённые ID при закрытии.
    const notesChangedRef = React.useRef(false);

    const afterAction = React.useCallback((noteId: number) => {
        dispatch(alertSuccess());
        if (viewerUpdates) return viewerUpdates.changed([noteId]);
        notesChangedRef.current = true;
        return loadNotes().then(() => undefined);
    }, [dispatch, loadNotes, viewerUpdates]);

    const handleHide = React.useCallback(() => {
        if (viewerUpdates) viewerUpdates.deactivate();
        else if (notesChangedRef.current) {
            notesChangedRef.current = false;
            onNotesChanged?.();
        }
        onHide();
    }, [onNotesChanged, onHide, viewerUpdates]);

    const handleComplete = React.useCallback((noteId: number, comment: string) => {
        if (viewerUpdates ? !viewerUpdates.beginMutation() : isActionLoading) return Promise.resolve();
        return new Promise<void>(resolve => {
            promiseWrapper(
                () => services.pDFNoteService.complete(noteId, comment),
                () => { afterAction(noteId).finally(() => { viewerUpdates?.finishMutation(); resolve(); }); },
                () => { viewerUpdates?.finishMutation(); resolve(); }
            );
        });
    }, [promiseWrapper, afterAction, isActionLoading, viewerUpdates]);

    const handleCancelCompletion = React.useCallback((noteId: number) => {
        if (viewerUpdates ? !viewerUpdates.beginMutation() : isActionLoading) return Promise.resolve();
        return new Promise<void>(resolve => {
            promiseWrapper(
                () => services.pDFNoteService.cancelCompletion(noteId),
                () => { afterAction(noteId).finally(() => { viewerUpdates?.finishMutation(); resolve(); }); },
                () => { viewerUpdates?.finishMutation(); resolve(); }
            );
        });
    }, [promiseWrapper, afterAction, isActionLoading, viewerUpdates]);

    const handleDownload = React.useCallback(() => {
        void downloadFile(attachmentUrl, attachment.Attachment?.Title ?? `file`);
    }, [downloadFile, attachmentUrl, attachment.Attachment?.Title]);

    const ready = file.blobUrl && highlights !== undefined && (!viewerUpdates || loadedViewerUpdates === viewerUpdates);

    const commonProps = {
        mode: 'review' as const,
        commentsPanel: ContractorCommentsPanel,
        noteLinkHref: () => null,
        contractorPermissions,
        onContractorComplete: handleComplete,
        onContractorCancelCompletion: handleCancelCompletion,
        initialHighlights: highlights ?? [],
        initialScrollToNoteId,
        noteObjectId: projectDocFolderId,
    };

    return (
        <Dialog
            header={
                <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-.01em' }}>
                        {attachment.Attachment?.Title ?? 'Просмотр документа'}
                    </span>
                    <button
                        type="button"
                        title={isDownloading ? `Скачивание…` : `Скачать файл`}
                        disabled={isDownloading}
                        style={{
                            width: 30, height: 30, flex: 'none',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: 'none', border: 'none', borderRadius: 8,
                            cursor: isDownloading ? 'wait' : 'pointer',
                            color: 'var(--text-tertiary, #6e6e73)', fontSize: 14,
                            opacity: isDownloading ? 0.6 : 1,
                        }}
                        onClick={handleDownload}
                    >
                        <i className={isDownloading ? `pi pi-spin pi-spinner` : `fa-solid fa-download`} />
                    </button>
                </span>
            }
            visible
            maximized
            modal
            closable
            onHide={handleHide}
            // Тема primereact гасит границу шапки (border-bottom: 0 none),
            // а по макету шапка отделена от области просмотра.
            // Dialog draggable по умолчанию — тема вешает cursor: move на шапку,
            // а сама шапка (кнопки, ссылки) не draggable-зона, так что курсор врёт.
            headerStyle={{ borderBottom: '1px solid var(--border-08, rgba(0,0,0,.08))', cursor: 'default' }}
            contentStyle={{ padding: 0, overflow: 'hidden' }}
        >
            {viewerUpdates && noteUpdatesError && (
                <div role="alert" style={{ padding: '8px 16px' }}>
                    Не удалось обновить замечания. Изменение сохранено.
                    <button type="button" onClick={() => { setNoteUpdatesError(false); void viewerUpdates.retry(); }}>Повторить</button>
                </div>
            )}
            {ready ? (
                isWord ? (
                    <WordViewer {...commonProps} url={file.blobUrl!} />
                ) : (
                    <PDFViewer {...commonProps} url={file.blobUrl!} workerSrc={WORKER_SRC} />
                )
            ) : (
                <DocumentLoading
                    phase={file.phase === 'ready' ? 'notes' : file.phase}
                    percent={file.percent}
                    loaded={file.loaded}
                    total={file.total}
                    error={file.error}
                    onRetry={file.retry}
                    onDownload={handleDownload}
                />
            )}
        </Dialog>
    );
};
